import { NgClass, NgStyle } from '@angular/common';
import { ChangeDetectionStrategy, Component, computed, forwardRef, signal } from '@angular/core';
import { IconComponent } from '@examdojo/core/icon';
import { NodeViewComponent } from '@examdojo/core/tiptap';
import type { EChartsOption } from 'echarts';
import { v4 as uuid } from 'uuid';
import { EchartsBlock } from '../../../model';
import { EchartsSvgComponent } from '../../../echarts/echarts-svg.component';
import { DragWithHandleOnlyDirective } from '../drag-with-handle-only.directive';
import { DEFAULT_ECHARTS_PROPS } from './echarts.model';
import { EchartsEditorComponent } from './echarts-editor.component';

const ECHARTS_DOCS_URL = 'https://echarts.apache.org/examples/en/index.html';

@Component({
  selector: 'dojo-echarts-node',
  standalone: true,
  imports: [NgClass, NgStyle, IconComponent, EchartsSvgComponent, EchartsEditorComponent],
  templateUrl: './echarts-node.component.html',
  styleUrl: './echarts-node.component.scss',
  hostDirectives: [DragWithHandleOnlyDirective],
  providers: [
    {
      provide: NodeViewComponent,
      useExisting: forwardRef(() => EchartsNodeComponent),
    },
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class EchartsNodeComponent extends NodeViewComponent<EchartsBlock> {
  readonly options = this.getProp('options');
  readonly heightProp = this.getProp('height');
  readonly customCss = this.getProp('custom_css');
  readonly customStyle = this.getProp('custom_style');

  readonly height = computed(() => this.heightProp() ?? 400);
  readonly showEditor = signal(false);
  readonly docsUrl = ECHARTS_DOCS_URL;

  readonly isEmpty = computed(() => {
    const opts = this.options();
    return !opts || Object.keys(opts).length === 0;
  });

  override getDefaults = (): EchartsBlock => ({
    ...DEFAULT_ECHARTS_PROPS,
    id: uuid(),
  });

  openEditor(): void {
    if (this.editor().isEditable) {
      this.showEditor.set(true);
    }
  }

  closeEditor(): void {
    this.showEditor.set(false);
  }

  onOptionsChange(update: { options: EChartsOption; height?: number }): void {
    this.updateProps({
      options: update.options,
      ...(update.height !== undefined && { height: update.height }),
    });
  }
}
